// src/pages/LessonFeedbackPage.js
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function LessonFeedbackPage() {
  const { classroomId, studentId, lessonId } = useParams();
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!classroomId || !lessonId || !studentId) {
      setError('Missing required parameters in the URL.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [{ data: lessonData, error: lessonError }, { data: progressData, error: progressError }] =
          await Promise.all([
            supabase
              .from('lessons')
              .select('title, reading, writing_prompt, level')
              .eq('id', lessonId)
              .single(),
            supabase
              .from('classroom_lesson_progress')
              .select('score, writing_response, teacher_feedback, teacher_score')
              .eq('student_id', studentId)
              .eq('classroom_id', classroomId)
              .eq('lesson_id', lessonId)
              .single(),
          ]);

        if (lessonError) throw lessonError;
        if (progressError) throw progressError;

        setLesson(lessonData);
        setProgress(progressData);
      } catch (err) {
        console.error(err);
        setError('Failed to load lesson or student progress.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [lessonId, studentId, classroomId]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <HeaderSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!lesson || !progress) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
          Lesson or progress not found.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8">
      {/* Header + breadcrumbs */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <nav className="text-sm text-gray-500">
            <ol className="inline-flex items-center gap-1">
              <li>
                <Link to={`/classrooms/student/${classroomId}`} className="hover:underline">
                  Classroom
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <span className="text-gray-700">Lesson Feedback</span>
              </li>
            </ol>
          </nav>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">{lesson.title}</h1>
          <p className="mt-1 text-sm text-gray-600">
            Level: <span className="font-medium">{lesson.level || '—'}</span>
          </p>
        </div>

        {/* Quick summary pill */}
        <div className="rounded-lg border bg-white px-4 py-3 shadow-sm">
          <p className="text-sm text-gray-600">Summary</p>
          <div className="mt-1 text-sm text-gray-800 space-x-3">
            <span>Quiz Score: <strong>{progress.score != null ? progress.score : '—'}</strong></span>
            <span>Teacher Score: <strong>{progress.teacher_score != null ? `${progress.teacher_score}/100` : '—'}</strong></span>
          </div>
        </div>
      </div>

      {/* Reading */}
      {lesson.reading && (
        <Card title="Reading" subtitle="Assigned passage for this lesson">
          <div className="prose max-w-none whitespace-pre-wrap text-gray-800">{lesson.reading}</div>
        </Card>
      )}

      {/* Writing prompt + student response */}
      {lesson.writing_prompt && (
        <Card title="Writing Prompt" subtitle="Original prompt and student response">
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-gray-700 italic whitespace-pre-wrap">
                {lesson.writing_prompt}
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900">Student Response</h4>
              <p className="mt-2 whitespace-pre-wrap text-gray-800">
                {progress.writing_response || 'No response submitted.'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Teacher feedback */}
      <Card title="Teacher Feedback" subtitle="Comments and evaluation">
        <div className="space-y-3">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="whitespace-pre-wrap text-gray-800">
              {progress.teacher_feedback || 'No feedback provided yet.'}
            </p>
          </div>

          <div className="text-sm text-gray-700">
            Teacher Grade:{' '}
            <span className="font-semibold">
              {progress.teacher_score != null ? `${progress.teacher_score} / 100` : 'Not graded'}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ---------- Reusable UI ---------- */

function Card({ title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
      </div>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}

function HeaderSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
      <div className="h-8 w-72 bg-gray-100 rounded animate-pulse" />
      <div className="h-4 w-56 bg-gray-100 rounded animate-pulse" />
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <div className="h-5 w-40 bg-gray-100 rounded animate-pulse" />
        <div className="mt-2 h-4 w-64 bg-gray-100 rounded animate-pulse" />
      </div>
      <div className="px-5 py-5 space-y-3">
        <div className="h-4 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
      </div>
    </div>
  );
}
